/* eslint-disable max-len */
import { Client } from '@stomp/stompjs';
import { Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { getUserId } from 'domains/user';
import { createUserMonitoringAction } from 'domains/webSockets/userBehaviourMonitoring/createUserMonitoringAction';
import { setupUserBehaviourMonitoringStompClient } from 'domains/webSockets/userBehaviourMonitoring/setupUserBehaviourMonitoringStompClient';
import { shouldTrackAction } from 'domains/webSockets/userBehaviourMonitoring/trackingFilter';
import { sendTrackingMessage } from 'domains/webSockets/utils/sendTrackingMessage';
import { isCollectingConnected, isConnected, isWsUserSessionInitialized } from 'domains/webSockets/WebSocket.store';
import { WebSocketAction } from 'utils';
import {
  WS_COLLECTING_CONNECT,
  WS_COLLECTING_DISCONNECT,
  WS_DISCONNECT,
  WS_INACTIVITY_DISCONNECT,
} from '../WebSocket.types';

/**
 * User behaviour monitoring:
 * Middleware for collecting all application redux actions with payloads for tracking
 * and sending it to service via a separate ws channel.
 */
export const userBehaviourMonitoringMiddleware = (): Middleware => {
  let stompClient: Client | null = null;

  return ({ dispatch, getState }: MiddlewareAPI) => (next: Dispatch) => (action: WebSocketAction) => {
    const result = next(action);

    switch (action.type) {
      case WS_COLLECTING_CONNECT: {
        if (isCollectingConnected(getState())) {
          console.warn('UserBehaviourMonitoring: WS connect received, but connection is already established');
          break;
        }
        stompClient?.deactivate();
        stompClient = setupUserBehaviourMonitoringStompClient(dispatch, getState);
        stompClient.activate();
        break;
      }

      case WS_COLLECTING_DISCONNECT:
      case WS_DISCONNECT:
      case WS_INACTIVITY_DISCONNECT: {
        stompClient?.deactivate();
        stompClient = null;
        break;
      }

      default: {
        if (stompClient?.connected && shouldTrackAction(action.type)) {
          const isSessionReady = isWsUserSessionInitialized(getState()) && isCollectingConnected(getState());
          const connected = isConnected(getState());
          const isUserId = getUserId(getState());

          if (isSessionReady && connected && isUserId) {
            const wsAction = createUserMonitoringAction(action, getState);
            sendTrackingMessage(stompClient, wsAction);
          }
        }
        break;
      }
    }

    return result;
  };
};
